import type { OTRModuleModel } from "../../models/OTRModuleModel"


export const OTR_IMPORT_DUPLICATE_JSON_KEYS = [
  "invoiceNumber",
  "insuredName",
  "policyNumber",
  "stateCode",
  "taxType",
  "transactionType",
  "invDate",
  "effectiveDate",
  "expirationDate",
  "grossPremium",
  "taxAmt",
  "client",
] as const

export type OtrImportDuplicatePayloadRow = {
  [K in (typeof OTR_IMPORT_DUPLICATE_JSON_KEYS)[number]]: string | undefined
}

function toPayloadString(v: string | number | null | undefined): string | undefined {
  if (v === null || v === undefined) return undefined
  const s = String(v).trim()
  return s === "" ? undefined : s
}

/** One row for CheckImportDuplicates API body (same fields as OPENJSON in checkImportDuplicates.sql). */
export function buildOtrImportDuplicatePayloadItem(item: OTRModuleModel): OtrImportDuplicatePayloadRow {
  const row: OtrImportDuplicatePayloadRow = {
    invoiceNumber: item.invoiceNumber,
    insuredName: item.insuredName,
    policyNumber: item.policyNumber,
    stateCode: item.homeState ?? (item as { stateCode?: string }).stateCode ?? "",
    taxType: item.taxType,
    transactionType: item.transactionType,
    invDate: item.invDate,
    effectiveDate: item.policyEffDate || (item as { effDate?: string }).effDate,
    expirationDate: item.policyExpDate,
    grossPremium: item.grossPremium,
    taxAmt: toPayloadString((item as { taxAmt?: string | number }).taxAmt),
    client: item.client,
  }
  return row
}

export function buildOtrImportDuplicatePayload(items: OTRModuleModel[]): OtrImportDuplicatePayloadRow[] {
  return items.map(buildOtrImportDuplicatePayloadItem)
}

/**
 * Canonical duplicate key for OTR in-file + CheckImportDuplicates only.
 * Removed-queue matching uses a separate payload and checkRemovedQueue.sql (unchanged).
 */
export function getOtrImportDuplicateKey(item: OTRModuleModel): string {
  const str = (v: string | number | undefined) => (v === undefined || v === null ? "" : String(v)).trim()
  const dateNorm = (v: string | undefined) => {
    const s = str(v)
    if (!s) return ""
    if (s.length >= 10 && s.indexOf("-") !== -1) return s.slice(0, 10)
    return s
  }
  const numNorm = (v: string | number | undefined) => {
    if (v === undefined || v === null) return ""
    const s = str(v).replace(/,/g, "").replace(/\$/g, "")
    const n = parseFloat(s)
    if (isNaN(n)) return s
    return n.toFixed(2)
  }
  const invNorm = (v: string | number | undefined) => {
    const s = str(v).replace(/,/g, "").replace(/\$/g, "")
    const n = parseFloat(s)
    if (!isNaN(n)) return n.toFixed(2)
    return s
  }

  const stateCode = str(item.homeState ?? (item as { stateCode?: string }).stateCode)
  // Align with buildOtrImportDuplicatePayload (use || so empty string falls back to effDate).
  const effectiveDate = item.policyEffDate || item.effDate
  const expirationDate = item.policyExpDate

  return [
    invNorm(item.invoiceNumber),
    str(item.insuredName),
    str(item.policyNumber),
    stateCode,
    str(item.taxType),
    str(item.transactionType),
    dateNorm(item.invDate),
    dateNorm(effectiveDate),
    dateNorm(expirationDate),
    numNorm(item.grossPremium),
    numNorm(item.taxAmt),
    str(item.client),
  ].join("|")
}

export type InFileDuplicateGroup = {
  indices: number[]
  /** Excel _rowNum when present, else 1-based import index */
  rowLabels: string[]
}

/** Groups of two or more rows that share the same duplicate key (0-based indices into `items`). */
export function findInFileDuplicateGroups(items: OTRModuleModel[]): InFileDuplicateGroup[] {
  const keyToIndices = new Map<string, number[]>()
  items.forEach((item, i) => {
    const k = getOtrImportDuplicateKey(item)
    if (!keyToIndices.has(k)) keyToIndices.set(k, [])
    keyToIndices.get(k)!.push(i)
  })
  const groups: InFileDuplicateGroup[] = []
  keyToIndices.forEach((indices) => {
    if (indices.length < 2) return
    const rowLabels = indices.map((i) => {
      const raw = (items[i] as { _rowNum?: string | number })._rowNum
      if (raw !== undefined && raw !== null && String(raw).trim() !== "") return String(raw)
      return String(i + 1)
    })
    groups.push({ indices, rowLabels })
  })
  return groups
}

/** Keep first occurrence per duplicate key; drop later rows in each group. */
export function filterInFileDuplicatesKeepFirst(items: OTRModuleModel[]): OTRModuleModel[] {
  const seen = new Set<string>()
  const out: OTRModuleModel[] = []
  items.forEach((item) => {
    const k = getOtrImportDuplicateKey(item)
    if (seen.has(k)) return
    seen.add(k)
    out.push(item)
  })
  return out
}
